import type { InvoiceDetail, InvoiceDisplayStatus, InvoiceUsage } from "@stb/api-contract"
import { Link } from "@tanstack/react-router"
import { AlertTriangleIcon, PlusIcon } from "lucide-react"
import { useMemo, useState } from "react"

import { Alert, AlertDescription } from "@/ui/alert"
import { Button } from "@/ui/button"
import { Card } from "@/ui/card"
import { Input } from "@/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs"
import { Muted } from "@/ui/typography"

import { daysOverdue, daysUntil, formatAud, formatDay } from "../../format"
import { InvoiceStatusBadge } from "../invoice-status-badge/invoice-status-badge"
import { QuotaBanner, quotaState } from "../quota-banner/quota-banner"

const TABS = [
  { value: "all", label: "All", statuses: null },
  { value: "draft", label: "Draft", statuses: ["draft", "not_sent", "sending"] },
  { value: "sent", label: "Sent", statuses: ["sent"] },
  { value: "part", label: "Part paid", statuses: ["partially_paid"] },
  { value: "overdue", label: "Overdue", statuses: ["overdue"] },
  { value: "paid", label: "Paid", statuses: ["paid"] },
] as const satisfies readonly {
  value: string
  label: string
  statuses: readonly InvoiceDisplayStatus[] | null
}[]

const UNPAID: readonly InvoiceDisplayStatus[] = ["sent", "partially_paid", "overdue"]

export interface InvoiceListProps {
  invoices: InvoiceDetail[]
  usage: InvoiceUsage
  /** Sydney `YYYY-MM-DD`; passed in so the page and its tests agree on "today". */
  today: string
  onRecordPayment: (invoice: InvoiceDetail) => void
}

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0)

function customerName(invoice: InvoiceDetail): string {
  return invoice.customer?.name ?? invoice.buyer_name ?? "No customer"
}

function DueCell({ invoice, today }: { invoice: InvoiceDetail; today: string }) {
  if (invoice.display_status === "draft" || invoice.display_status === "not_sent") {
    return <span className="text-muted-foreground">—</span>
  }
  const overdueDays = daysOverdue(invoice.due_date, today)
  const note =
    invoice.display_status === "overdue" ? (
      <span className="text-destructive text-xs font-medium">
        {overdueDays} {overdueDays === 1 ? "day" : "days"} overdue
      </span>
    ) : UNPAID.includes(invoice.display_status) && daysUntil(invoice.due_date, today) > 0 ? (
      <span className="text-muted-foreground text-xs">
        in {daysUntil(invoice.due_date, today)} days
      </span>
    ) : null
  return (
    <div className="flex flex-col">
      <span>{formatDay(invoice.due_date)}</span>
      {note}
    </div>
  )
}

const TH = "px-4 py-2.5 text-left text-xs font-medium text-muted-foreground"

function TableHead() {
  return (
    <thead className="border-b">
      <tr>
        <th scope="col" className={TH}>
          Number · issued
        </th>
        <th scope="col" className={TH}>
          Customer
        </th>
        <th scope="col" className={TH}>
          Due
        </th>
        <th scope="col" className={`${TH} text-right`}>
          Amount
        </th>
        <th scope="col" className={TH}>
          Status
        </th>
        <th scope="col" className={`${TH} text-right`}>
          <span className="sr-only">Actions</span>
        </th>
      </tr>
    </thead>
  )
}

function Kpi({
  label,
  value,
  kpi,
  note,
}: {
  label: string
  value: string
  kpi: string
  note: string
}) {
  return (
    <Card className="gap-1 px-5 py-4">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span data-kpi={kpi} className="text-2xl font-semibold tabular-nums">
        {value}
      </span>
      <span className="text-muted-foreground text-xs">{note}</span>
    </Card>
  )
}

/** Collect payment counts as an invoice (Q5), so it is off when the monthly quota is used up. */
function CollectPaymentButton({ blocked, describedBy }: { blocked: boolean; describedBy: string }) {
  if (blocked) {
    return (
      <Button variant="outline" disabled aria-describedby={describedBy}>
        Collect payment
      </Button>
    )
  }
  return (
    <Button asChild variant="outline">
      <Link to="/invoices/collect">Collect payment</Link>
    </Button>
  )
}

function NewInvoiceButton({ blocked, describedBy }: { blocked: boolean; describedBy: string }) {
  if (blocked) {
    return (
      <Button disabled aria-describedby={describedBy}>
        <PlusIcon aria-hidden="true" />
        New invoice
      </Button>
    )
  }
  return (
    <Button asChild>
      <Link to="/invoices/new">
        <PlusIcon aria-hidden="true" />
        New invoice
      </Link>
    </Button>
  )
}

export function InvoiceList({ invoices, usage, today, onRecordPayment }: InvoiceListProps) {
  const [tab, setTab] = useState<string>("all")
  const [query, setQuery] = useState("")
  const blocked = quotaState(usage) === "full"

  const counts = useMemo(
    () =>
      Object.fromEntries(
        TABS.map((entry) => [
          entry.value,
          entry.statuses
            ? invoices.filter((invoice) =>
                (entry.statuses as readonly string[]).includes(invoice.display_status),
              ).length
            : invoices.length,
        ]),
      ),
    [invoices],
  )

  const month = today.slice(0, 7)
  const unpaid = invoices.filter((invoice) => UNPAID.includes(invoice.display_status))
  const overdue = invoices.filter((invoice) => invoice.display_status === "overdue")
  const paidThisMonth = sum(
    invoices.flatMap((invoice) =>
      invoice.payments
        .filter((payment) => payment.received_on.startsWith(month))
        .map((payment) => payment.amount_minor),
    ),
  )

  const active = TABS.find((entry) => entry.value === tab) ?? TABS[0]
  const needle = query.trim().toLowerCase()
  const visible = invoices.filter((invoice) => {
    if (active.statuses && !(active.statuses as readonly string[]).includes(invoice.display_status))
      return false
    if (!needle) return true
    return (
      (invoice.number ?? "").toLowerCase().includes(needle) ||
      customerName(invoice).toLowerCase().includes(needle)
    )
  })

  return (
    <main className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
          <Muted>
            {invoices.length} {invoices.length === 1 ? "invoice" : "invoices"}
          </Muted>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/invoices/branding">Branding</Link>
          </Button>
          <CollectPaymentButton blocked={blocked} describedBy="invoice-quota" />
          <NewInvoiceButton blocked={blocked} describedBy="invoice-quota" />
        </div>
      </header>

      <QuotaBanner usage={usage} id="invoice-quota" />

      <div className="grid gap-4 sm:grid-cols-3">
        <Kpi
          label="Outstanding"
          kpi="outstanding"
          value={formatAud(sum(unpaid.map((invoice) => invoice.outstanding_minor)))}
          note={`${unpaid.length} unpaid ${unpaid.length === 1 ? "invoice" : "invoices"}`}
        />
        <Kpi
          label="Overdue"
          kpi="overdue"
          value={formatAud(sum(overdue.map((invoice) => invoice.outstanding_minor)))}
          note={`${overdue.length} ${overdue.length === 1 ? "invoice" : "invoices"}`}
        />
        <Kpi
          label="Paid this month"
          kpi="paid-month"
          value={formatAud(paidThisMonth)}
          note="Payments received"
        />
      </div>

      {invoices.length === 0 ? (
        <Card className="items-center gap-3 px-6 py-12 text-center">
          <h2 className="text-lg font-semibold">No invoices yet</h2>
          <Muted className="max-w-sm">
            Create an invoice, send it as a PDF and record payments. Invoices for a GST-registered
            business are issued as tax invoices.
          </Muted>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild>
              <Link to="/invoices/new">Create your first invoice</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/customers">Add a customer first</Link>
            </Button>
          </div>
        </Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList aria-label="Invoice status" className="h-auto flex-wrap">
              {TABS.map((entry) => (
                <TabsTrigger key={entry.value} value={entry.value}>
                  {entry.label}
                  <span className="text-muted-foreground tabular-nums">{counts[entry.value]}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            <Input
              type="search"
              aria-label="Search number or customer"
              placeholder="Search number or customer"
              className="max-w-xs"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <TabsContent value={tab}>
            <Card className="gap-0 overflow-x-auto py-0">
              <table className="w-full text-sm">
                <caption className="sr-only">Invoices</caption>
                <TableHead />
                <tbody>
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted-foreground px-4 py-8 text-center">
                        No invoices match.
                      </td>
                    </tr>
                  )}
                  {visible.map((invoice) => {
                    const isDraft = invoice.status === "draft"
                    return (
                      <tr key={invoice.id} className="border-b last:border-b-0">
                        <td className="px-4 py-3">
                          {invoice.number ? (
                            <div className="flex flex-col">
                              <Link
                                to="/invoices/$id"
                                params={{ id: invoice.id }}
                                className="font-medium underline-offset-4 hover:underline"
                              >
                                {invoice.number}
                              </Link>
                              <span className="text-muted-foreground text-xs">
                                {formatDay(invoice.issue_date)}
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col">
                              <span className="text-muted-foreground">—</span>
                              <span className="text-muted-foreground text-xs">Not numbered</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">{customerName(invoice)}</td>
                        <td className="px-4 py-3">
                          <DueCell invoice={invoice} today={today} />
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          <div className="flex flex-col items-end">
                            <span>{formatAud(invoice.total_minor)}</span>
                            {invoice.display_status === "partially_paid" ||
                            (invoice.display_status === "overdue" && invoice.paid_minor > 0) ? (
                              <span className="text-muted-foreground text-xs">
                                {formatAud(invoice.outstanding_minor)} owing
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <InvoiceStatusBadge status={invoice.display_status} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isDraft ? (
                            <Button asChild size="sm" variant="outline">
                              <Link to="/invoices/$id" params={{ id: invoice.id }}>
                                Edit draft
                              </Link>
                            </Button>
                          ) : UNPAID.includes(invoice.display_status) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              aria-label={`Record payment for ${invoice.number ?? "invoice"}`}
                              onClick={() => onRecordPayment(invoice)}
                            >
                              Record payment
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </main>
  )
}

/** Loading state: the page frame and the table header stay, rows pulse. */
export function InvoiceListSkeleton() {
  return (
    <main className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8">
      <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
      <div role="status" aria-label="Loading invoices" aria-busy="true">
        <Card className="gap-0 overflow-hidden py-0">
          <table className="w-full text-sm">
            <TableHead />
            <tbody>
              {[0, 1, 2].map((row) => (
                <tr key={row} className="border-b last:border-b-0">
                  {[0, 1, 2, 3, 4, 5].map((cell) => (
                    <td key={cell} className="px-4 py-4">
                      <div className="bg-muted h-4 w-3/4 animate-pulse rounded motion-reduce:animate-none" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </main>
  )
}

export function InvoiceListError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8">
      <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
      <Alert variant="destructive">
        <AlertTriangleIcon aria-hidden="true" />
        <AlertDescription>
          <p>{message}</p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    </main>
  )
}
